/**
 * Variant ↔ media assignment helpers.
 *
 * One image can be the image for MANY variants, while a variant shows exactly ONE
 * image. The backend models this with a junction table and returns `variant_ids`
 * (array, authoritative) alongside `variant_id` (legacy scalar = first element only).
 *
 * Reading the scalar sees just one variant, so a shared image looks unassigned to
 * every variant but the first. Writing the scalar can only record one, so assigning a
 * shared image to a second variant silently unassigns the first. These helpers exist so
 * no screen has to remember that: read through `mediaVariantIds`, write through
 * `assignVariantToMedia`, and normalize incoming API rows through `normalizeMediaItem`
 * so `variant_ids` survives the trip into component state.
 */

/**
 * Variant ids currently showing this media item. Always an array.
 * Falls back to the legacy scalar so pre-migration payloads still read correctly.
 */
export const mediaVariantIds = (media) => {
  if (!media) return [];
  if (Array.isArray(media.variant_ids)) return media.variant_ids.filter(Boolean);
  return media.variant_id ? [media.variant_id] : [];
};

/** Whether `variantId` is one of the variants showing this media item. */
export const isVariantOnMedia = (media, variantId) =>
  !!variantId && mediaVariantIds(media).some((id) => String(id) === String(variantId));

/** Whether this image is unassigned — a pure gallery image belonging to no variant. */
export const isGalleryOnlyMedia = (media) => mediaVariantIds(media).length === 0;

/**
 * Keep `variant_ids` when copying an API media row into component state.
 *
 * Normalizers that spread only the scalar were where multi-assignment was lost before
 * any UI code ran, which made every downstream read look like a separate bug.
 */
export const normalizeMediaItem = (media, extra = {}) => {
  const ids = mediaVariantIds(media);
  return {
    ...media,
    variant_ids: ids,
    variant_id: ids[0] || null,
    ...extra,
  };
};

/** Media rows to show for a variant: its own image plus the unassigned gallery. */
export const mediaForVariantView = (mediaList, variantId) =>
  (mediaList || []).filter(
    (m) => isGalleryOnlyMedia(m) || isVariantOnMedia(m, variantId)
  );

/** The media row a variant is currently showing, or null. */
export const findMediaForVariant = (mediaList, variantId) =>
  (mediaList || []).find((m) => isVariantOnMedia(m, variantId)) || null;

/**
 * Point `variantId` at `imageId`, returning the updated media list.
 *
 * Additive by design: the variant releases whatever image it held (a variant shows one
 * image) and joins the chosen one WITHOUT displacing the other variants already on it.
 * Pass `imageId = null` to unassign.
 */
export const assignVariantToMedia = (mediaList, variantId, imageId) =>
  (mediaList || []).map((m) => {
    const current = mediaVariantIds(m);
    let ids = current.filter((id) => String(id) !== String(variantId));
    if (imageId && String(m.id) === String(imageId)) ids = [...ids, variantId];

    const unchanged =
      ids.length === current.length &&
      ids.every((id, i) => String(id) === String(current[i]));
    if (unchanged) return m;

    return { ...m, variant_ids: ids, variant_id: ids[0] || null };
  });

/**
 * Media item shaped for an API payload.
 *
 * Sends the plural as the source of truth and the scalar as a legacy mirror, so a
 * consumer still reading `variant_id` sees something sensible rather than nothing.
 */
export const mediaPayloadItem = (media, index) => {
  const ids = mediaVariantIds(media);
  const item = {
    url: media.url,
    alt_text: media.alt || media.alt_text || '',
    variant_ids: ids,
    variant_id: ids[0] || null,
    position: media.position ?? index,
  };
  if (media.id && !String(media.id).startsWith('temp-')) item.id = media.id;
  return item;
};

/**
 * Names of the variants on this image, excluding `excludeVariantId`.
 * Used for the picker badge: "Red" for one, "3 variants" for several.
 */
export const otherVariantNames = (media, allVariants, excludeVariantId) =>
  mediaVariantIds(media)
    .filter((id) => String(id) !== String(excludeVariantId))
    .map((id) => (allVariants || []).find((v) => String(v.id || v.tempId) === String(id)))
    .filter(Boolean)
    .map((v) => v.variant_name || v.sku)
    .filter(Boolean);
